/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module ItemsModule
 */

import {
    AviPermissionResource,
    ITenantUsageDetail,
    ITenantUsageSummary,
} from 'generated-types';

import { Item } from 'ajs/modules/data-model/factories/item.factory';

interface ITenantUsageConfig extends ITenantUsageSummary {
    // Name property for IItemGenericConfig compatibility
    name?: string;
}

/**
 * Interface for tenant usage data structure from API.
 */
interface ITenantUsageData {
    config: ITenantUsageConfig;
}

/**
 * @description Tenant usage item for tenant usage list.
 * @author Shubham Bindal
 */
export class TenantUsageItem extends Item<ITenantUsageConfig> {
    public getConfig: () => ITenantUsageConfig;
    public data: ITenantUsageData;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'tenantUsage',
            windowElement: 'lazy-load',
            permissionName: AviPermissionResource.PERMISSION_SYSTEMCONFIGURATION,
            ...args,
        };

        super(extendedArgs);
    }

    /**
     * Get tenant name.
     */
    public get tenantName(): string {
        return this.getConfig()?.tenant_name ?? '';
    }

    /**
     * Get tenant uuid.
     */
    public get tenantUuid(): string {
        return this.getConfig()?.tenant_uuid ?? '';
    }

    /**
     * Get usage details.
     */
    public get usageDetails(): ITenantUsageDetail {
        return this.getConfig()?.usage;
    }

    /**
     * Get used units.
     */
    public get usedUnit(): number {
        return this.getConfig()?.usage?.used_service_units ?? 0;
    }

    /**
     * Get unit limit.
     */
    public get unitLimit(): number {
        return this.getConfig()?.usage?.quota_limit ?? 0;
    }

    /** @override */
    // eslint-disable-next-line no-underscore-dangle
    protected getIdFromData_(data: ITenantUsageData): string {
        return data?.config?.tenant_uuid ?? '';
    }
}
